import React from 'react';
import { Button } from './Button';
import { useRotatingGlow } from '../../hooks/useRotatingGlow';
import { cn } from '../../lib/utils';

/**
 * Button component with rotating glow effect
 * Based on the technique from: https://dev.to/shofol/create-eye-catching-button-effect-with-rotating-glow-animation-2a92
 * 
 * @param {Object} props - Button props plus glow-specific options
 * @param {string} props.glowColor - Color for the glow effect (default: 'orange')
 * @param {number} props.glowSpeed - Animation speed (default: 1)
 * @param {boolean} props.enableGlow - Whether to enable the glow effect (default: true)
 * @param {string} props.className - Additional CSS classes
 * @param {React.ReactNode} props.children - Button content
 */
const RotatingGlowButton = React.forwardRef(
  (
    {
      glowColor = 'orange',
      glowSpeed = 1,
      enableGlow = true,
      className,
      children,
      ...buttonProps
    },
    ref
  ) => {
    const glowRef = useRotatingGlow({ speed: glowSpeed, enabled: enableGlow });
    const combinedRef = React.useCallback(
      (node) => {
        glowRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [glowRef, ref]
    );

    // Map glow color to Tailwind color values
    const glowColorMap = {
      orange: {
        light: 'rgb(251, 146, 60)', // orange-400
        dark: 'rgb(249, 115, 22)', // orange-500
      },
      blue: {
        light: 'rgb(96, 165, 250)', // blue-400
        dark: 'rgb(59, 130, 246)', // blue-500
      },
      purple: {
        light: 'rgb(196, 181, 253)', // purple-400
        dark: 'rgb(168, 85, 247)', // purple-500
      },
      green: {
        light: 'rgb(74, 222, 128)', // green-400
        dark: 'rgb(34, 197, 94)', // green-500
      },
    };

    const color = glowColorMap[glowColor] || glowColorMap.orange;
    const glowColorValue = color.light;

    // Get the button's background color based on variant
    // This ensures the conic gradient border shows through properly
    const getBackgroundColor = () => {
      const variant = buttonProps.variant || 'default';
      
      switch (variant) {
        case 'secondary':
          return 'hsl(var(--secondary))';
        case 'primary':
        case 'default':
          return 'hsl(var(--primary))';
        case 'destructive':
          return 'hsl(var(--destructive))';
        case 'outline':
          return 'hsl(var(--background))';
        case 'ghost':
          return 'transparent';
        default:
          return 'hsl(var(--background))';
      }
    };

    return (
      <Button
        ref={combinedRef}
        className={cn(
          'relative',
          enableGlow && 'rotating-glow-button border-2 border-transparent',
          className
        )}
        style={
          enableGlow
            ? {
                background: `linear-gradient(${getBackgroundColor()}, ${getBackgroundColor()}) padding-box,
                  conic-gradient(
                    from var(--glow-angle, 0deg),
                    transparent,
                    ${glowColorValue} 10%,
                    transparent 20%
                  ) border-box`,
                '--glow-angle': '0deg',
              }
            : undefined
        }
        {...buttonProps}
      >
        {children}
      </Button>
    );
  }
);

RotatingGlowButton.displayName = 'RotatingGlowButton';

export { RotatingGlowButton };
